<template>
  <ContentModal v-model="visible" title="Sign-up Notification Summary" size="2xl">
    <template #default>
      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-info" role="status"><span class="visually-hidden">Loading...</span></div>
        <p class="mt-2 text-muted">Loading summary...</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="text-center py-5 text-danger">
        <i class="material-icons" style="font-size:48px;">error_outline</i>
        <p class="mt-2">{{ error }}</p>
        <button class="btn btn-sm btn-outline-primary" @click="fetchSummary">Retry</button>
      </div>

      <!-- Content -->
      <div v-else-if="summaryData">
        <!-- Workgroup Header -->
        <div class="d-flex align-items-center mb-3 p-3 rounded" style="background:#f0f7ff;border-left:4px solid #2563eb;">
          <div>
            <h6 class="mb-1">{{ summaryData.workgroup?.name }}</h6>
            <span class="text-xs text-muted">
              {{ summaryData.activity?.name }} &mdash;
              {{ summaryData.instance?.start_date_formatted }} {{ summaryData.instance?.start_time_formatted }}
            </span>
            <span class="badge bg-gradient-info ms-2">Skill: {{ summaryData.workgroup?.skill || '-' }}</span>
            <span class="badge ms-1" :class="summaryData.workgroup?.is_full ? 'bg-gradient-success' : 'bg-gradient-warning'">
              {{ summaryData.workgroup?.is_full ? 'All positions filled' : `${summaryData.workgroup?.persons_filled || 0} / ${summaryData.workgroup?.persons_needed || 0} filled` }}
            </span>
          </div>
        </div>

        <!-- Full workgroup notice -->
        <div v-if="summaryData.workgroup?.is_full" class="alert alert-success d-flex align-items-center mb-3 py-2" role="alert" style="font-size:13px;">
          <i class="material-icons me-2" style="font-size:20px;">check_circle</i>
          <div>All positions are <strong>filled</strong>. No further sign-up notification emails will be sent for this workgroup.</div>
        </div>

        <!-- Signup mode dates -->
        <div v-if="summaryData.workgroup?.convert_to_signup_date || summaryData.workgroup?.create_date" class="d-flex flex-wrap gap-3 mb-3">
          <div v-if="summaryData.workgroup?.convert_to_signup_date" class="p-2 rounded border flex-fill" style="font-size:12px;background:#fff7ed;border-color:#fed7aa !important;">
            <i class="material-icons text-warning" style="font-size:16px;vertical-align:middle;">transform</i>
            <strong>Converted to sign-up:</strong> {{ summaryData.workgroup.convert_to_signup_date }}
          </div>
          <div v-else-if="summaryData.workgroup?.create_date" class="p-2 rounded border flex-fill" style="font-size:12px;background:#f0fdf4;border-color:#bbf7d0 !important;">
            <i class="material-icons text-success" style="font-size:16px;vertical-align:middle;">person_add</i>
            <strong>Sign-up only since creation:</strong> {{ summaryData.workgroup.create_date }}
          </div>
          <div v-if="!summaryData.workgroup?.convert_to_signup_date && !summaryData.workgroup?.create_date" class="p-2 rounded border flex-fill" style="font-size:12px;background:#f1f5f9;">
            <i class="material-icons text-secondary" style="font-size:16px;vertical-align:middle;">info</i>
            Date information not available (pre-existing workgroup)
          </div>
        </div>

        <!-- How Sign-up Notifications Work -->
        <div class="card mb-3 border-0 shadow-sm">
          <div class="card-body">
            <div class="d-flex align-items-center mb-2 cursor-pointer" @click="showHowItWorks = !showHowItWorks">
              <i class="material-icons text-info me-2">info</i>
              <h6 class="mb-0 text-sm">How Sign-up Notifications Work</h6>
              <i class="material-icons ms-auto text-secondary">{{ showHowItWorks ? 'expand_less' : 'expand_more' }}</i>
            </div>
            <div v-if="showHowItWorks" class="mt-2" style="font-size:13px;color:#475569;line-height:1.7;">
              <p class="mb-2">
                <strong>Sign-up notification emails</strong> are sent automatically by the scheduling engine
                (<code>sendsignupemail</code>) to volunteers who have the required skill for a sign-up only workgroup.
              </p>
              <p class="mb-2"><strong>Frequency:</strong> Each volunteer has a <em>Sign-up Range Days</em> setting
                (default: <strong>{{ summaryData.defaults?.signup_range_days }} days</strong>). A new sign-up email is only
                sent once that many days have passed since the last sign-up email for that location.</p>
              <p class="mb-2"><strong>Span:</strong> The email includes sign-up opportunities within the next
                <em>Sign-up Span Days</em> (default: <strong>{{ summaryData.defaults?.signup_span_days }} days</strong>).
                Volunteers can override both values in their notification settings.</p>
              <p class="mb-2"><strong>Criteria for sending a notification:</strong></p>
              <ul class="mb-2 ps-3">
                <li>The volunteer must have the required skill for the workgroup</li>
                <li>The volunteer must be <strong>available</strong> during the assignment date/time (unavailable volunteers are skipped)</li>
                <li>The volunteer's last sign-up email date + range days must be in the past</li>
                <li>The workgroup must be active, sign-up only, and have openings</li>
                <li>The volunteer must not already be assigned or have declined</li>
                <li>Notification preferences (User Rule #4 — Instance Assignment) can further exclude a person</li>
              </ul>
              <p class="mb-0"><strong>Note:</strong> Volunteers who are <strong>not available</strong> during the assignment
                date/time will <strong>not</strong> receive sign-up email notifications for that assignment.</p>
            </div>
          </div>
        </div>

        <!-- Summary Stats -->
        <div class="row mb-3">
          <div class="col-md-3 col-6 mb-2">
            <div class="p-3 rounded text-center" style="background:#f0fdf4;">
              <div class="fw-bold text-lg" style="color:#16a34a;">{{ summaryData.summary?.available_count || 0 }}</div>
              <div class="text-xs text-muted">Available</div>
            </div>
          </div>
          <div class="col-md-3 col-6 mb-2">
            <div class="p-3 rounded text-center" style="background:#fef2f2;">
              <div class="fw-bold text-lg" style="color:#dc2626;">{{ summaryData.summary?.unavailable_count || 0 }}</div>
              <div class="text-xs text-muted">Unavailable</div>
            </div>
          </div>
          <div class="col-md-3 col-6 mb-2">
            <div class="p-3 rounded text-center" style="background:#eff6ff;">
              <div class="fw-bold text-lg" style="color:#2563eb;">{{ summaryData.summary?.assigned_count || 0 }}</div>
              <div class="text-xs text-muted">Already Assigned</div>
            </div>
          </div>
          <div class="col-md-3 col-6 mb-2">
            <div class="p-3 rounded text-center" style="background:#fefce8;">
              <div class="fw-bold text-lg" style="color:#ca8a04;">{{ summaryData.summary?.eligible_for_notification_count || 0 }}</div>
              <div class="text-xs text-muted">Eligible for Next Notification</div>
            </div>
          </div>
        </div>

        <!-- Filter controls -->
        <div class="d-flex align-items-center gap-2 mb-3 flex-wrap">
          <input type="text" class="form-control form-control-sm" style="max-width:240px;"
            placeholder="Search by name or email..." v-model="searchTerm" />
          <select class="form-select form-select-sm" style="max-width:160px;" v-model="availabilityFilter">
            <option value="all">All Volunteers</option>
            <option value="available">Available Only</option>
            <option value="unavailable">Unavailable Only</option>
          </select>
          <span class="text-xs text-muted ms-auto">{{ filteredPersons.length }} of {{ summaryData.persons?.length || 0 }} volunteers</span>
        </div>

        <!-- Persons Table -->
        <div class="table-responsive">
          <table class="table table-sm table-hover align-middle">
            <thead class="table-light">
              <tr>
                <th class="text-xs text-uppercase">Name</th>
                <th class="text-xs text-uppercase">Email</th>
                <th class="text-xs text-uppercase">Available</th>
                <th class="text-xs text-uppercase">Assigned</th>
                <th class="text-xs text-uppercase">Last Signup Email</th>
                <th class="text-xs text-uppercase">Range Days</th>
                <th class="text-xs text-uppercase">Next Notification</th>
                <th class="text-xs text-uppercase">Will Be Notified</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="person in filteredPersons" :key="person.person_id">
                <td class="text-sm fw-semibold">{{ person.name }}</td>
                <td class="text-sm">{{ person.email }}</td>
                <td>
                  <span :class="person.is_available ? 'badge bg-gradient-success' : 'badge bg-gradient-danger'">
                    {{ person.is_available ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td>
                  <span v-if="person.is_assigned" class="badge bg-gradient-info">Assigned</span>
                  <span v-else-if="person.is_declined" class="badge bg-gradient-warning">Declined</span>
                  <span v-else class="text-muted text-xs">-</span>
                </td>
                <td class="text-xs">{{ person.last_signup_email_date || 'Never' }}</td>
                <td class="text-xs">
                  {{ person.signup_range_days }}
                  <span v-if="person.is_custom_range" class="badge bg-gradient-secondary ms-1" style="font-size:10px;">custom</span>
                </td>
                <td class="text-xs">{{ person.next_notification_date }}</td>
                <td>
                  <span v-if="summaryData.workgroup?.is_full" class="text-xs text-muted">N/A (full)</span>
                  <span v-else-if="person.is_assigned || person.is_declined" class="text-muted text-xs">N/A</span>
                  <span v-else-if="!person.is_available" class="text-xs text-danger">Skipped (unavailable)</span>
                  <span v-else :class="person.will_be_notified ? 'badge bg-gradient-success' : 'badge bg-gradient-secondary'">
                    {{ person.will_be_notified ? 'Yes' : 'Not yet' }}
                  </span>
                </td>
              </tr>
              <tr v-if="filteredPersons.length === 0">
                <td colspan="8" class="text-center text-muted py-4">No volunteers found matching your criteria.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </ContentModal>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import ContentModal from "@/components/Modal/ContentModal.vue";
import api from "@/config/api";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  locationId: { type: [String, Number], required: true },
  aiwgId: { type: [String, Number], default: null },
});

const emit = defineEmits(["update:modelValue"]);

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

const loading = ref(false);
const error = ref(null);
const summaryData = ref(null);
const searchTerm = ref("");
const availabilityFilter = ref("all");
const showHowItWorks = ref(false);

const filteredPersons = computed(() => {
  if (!summaryData.value?.persons) return [];
  let list = summaryData.value.persons;
  if (availabilityFilter.value === "available") {
    list = list.filter((p) => p.is_available);
  } else if (availabilityFilter.value === "unavailable") {
    list = list.filter((p) => !p.is_available);
  }
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    list = list.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        p.email.toLowerCase().includes(term)
    );
  }
  return list;
});

async function fetchSummary() {
  if (!props.aiwgId || !props.locationId) return;
  loading.value = true;
  error.value = null;
  try {
    const response = await api.get(
      `/locations/${props.locationId}/signup-only-workgroups/${props.aiwgId}/summary`
    );
    const data = response.data?.data || response.data;
    summaryData.value = data;
  } catch (err) {
    console.error("Error fetching signup summary:", err);
    error.value = err.response?.data?.message || "Failed to load summary";
  } finally {
    loading.value = false;
  }
}

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal && props.aiwgId) {
      fetchSummary();
    }
  }
);
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
